import { useState, useCallback } from 'react';

/**
 * Custom hook for JetSetter passenger management
 * @returns {Object} Passengers state and methods
 */
const usePassengers = () => {
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mock passengers data
  const mockPassengers = [
    {
      id: 'PASS001',
      firstName: 'John',
      lastName: 'Doe',
      dateOfBirth: '1990-01-15',
      nationality: 'US',
      passportNumber: 'US123456789',
      passportExpiry: '2030-01-15',
      gender: 'male',
      email: 'john.doe@example.com',
      phone: '+1-555-123-4567',
      frequentFlyerNumber: 'JS123456789',
      specialRequests: '',
      isDefault: true
    },
    {
      id: 'PASS002',
      firstName: 'Jane',
      lastName: 'Doe',
      dateOfBirth: '1992-05-20',
      nationality: 'US',
      passportNumber: 'US987654321',
      passportExpiry: '2029-05-20',
      gender: 'female',
      email: 'jane.doe@example.com',
      phone: '+1-555-987-6543',
      frequentFlyerNumber: '',
      specialRequests: 'Vegetarian meal',
      isDefault: false
    }
  ];

  const loadPassengers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setPassengers(mockPassengers);
    } catch (err) {
      console.error('Failed to load passengers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const addPassenger = useCallback(async (passengerData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newPassenger = {
        id: `PASS${Date.now()}`,
        ...passengerData,
        isDefault: passengers.length === 0 // First passenger is default
      };
      
      setPassengers(prev => [...prev, newPassenger]);
      return newPassenger;
    } catch (err) {
      console.error('Failed to add passenger:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [passengers.length]);

  const updatePassenger = useCallback(async (passengerId, passengerData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const updatedPassenger = { ...passengerData, id: passengerId };
      
      setPassengers(prev => prev.map(passenger => 
        passenger.id === passengerId ? updatedPassenger : passenger
      ));
      
      return updatedPassenger;
    } catch (err) {
      console.error('Failed to update passenger:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePassenger = useCallback(async (passengerId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 600));
      
      setPassengers(prev => prev.filter(passenger => passenger.id !== passengerId));
    } catch (err) {
      console.error('Failed to delete passenger:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const setDefaultPassenger = useCallback(async (passengerId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      setPassengers(prev => prev.map(passenger => ({
        ...passenger,
        isDefault: passenger.id === passengerId
      })));
    } catch (err) {
      console.error('Failed to set default passenger:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDefaultPassenger = useCallback(() => {
    return passengers.find(passenger => passenger.isDefault) || passengers[0];
  }, [passengers]);

  const validatePassenger = useCallback((passengerData) => {
    const errors = {};
    
    if (!passengerData.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!passengerData.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!passengerData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    }
    
    if (!passengerData.nationality) {
      errors.nationality = 'Nationality is required';
    }
    
    if (!passengerData.passportNumber?.trim()) {
      errors.passportNumber = 'Passport number is required';
    }
    
    if (!passengerData.passportExpiry) {
      errors.passportExpiry = 'Passport expiry date is required';
    }
    
    if (!passengerData.gender) {
      errors.gender = 'Gender is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, []);

  return {
    passengers,
    loading,
    error,
    loadPassengers,
    addPassenger,
    updatePassenger,
    deletePassenger,
    setDefaultPassenger,
    getDefaultPassenger,
    validatePassenger
  };
};

export default usePassengers;